import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { BottomNav } from '@/components/layout/BottomNav';
import { MobileNavDrawer } from '@/components/layout/MobileNavDrawer';
import { SideNav } from '@/components/layout/SideNav';
import { VersionFooter } from '@/components/layout/VersionFooter';
import { DemoBanner } from '@/components/layout/DemoBanner';
import { UpdatePrompt } from '@/components/layout/UpdatePrompt';
import PasskeyEnrollPrompt from '@/components/PasskeyEnrollPrompt';
import { GlossaryProvider } from '@/components/ui/Glossary';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { SpinnerRing } from '@/components/ui/AsyncGuard';
import { DevToolbar } from '@/components/ui/DevToolbar';
import { VoiceFAB } from '@/components/ui/VoiceFAB';
import { ToastProvider } from '@/components/ui/Toast';
import { useAppStore } from '@/stores/useAppStore';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RoleGuard } from '@/components/auth/RoleGuard';
import { ManagerRouteGuard } from '@/components/auth/ManagerRouteGuard';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useRole } from '@/hooks/useRole';

// Critical path — loaded immediately
import CommandCenter from '@/pages/CommandCenter';
import Login from '@/pages/Login';

// Lazy-loaded pages — split into separate chunks
const NotFound = lazy(() => import('@/pages/NotFound'));
const Capture = lazy(() => import('@/pages/Capture'));
const Expenses = lazy(() => import('@/pages/Expenses'));
const Compliance = lazy(() => import('@/pages/Compliance'));
const ComplianceChecklist = lazy(() => import('@/pages/ComplianceChecklist'));
const ComplianceFacts = lazy(() => import('@/pages/ComplianceFacts'));
const LocumsClassifications = lazy(() => import('@/pages/LocumsClassifications'));
const LocumsClassify = lazy(() => import('@/pages/LocumsClassify'));
const LocumsClassificationDetail = lazy(() => import('@/pages/LocumsClassificationDetail'));
const Reports = lazy(() => import('@/pages/Reports'));
const Consolidated = lazy(() => import('@/pages/Consolidated'));
const DocumentView = lazy(() => import('@/pages/DocumentView'));
const EmployeeDocumentView = lazy(() => import('@/pages/DocumentView').then(m => ({ default: m.EmployeeDocumentView })));
const TaxPlanning = lazy(() => import('@/pages/TaxPlanning'));
const Tax1099Input = lazy(() => import('@/pages/Tax1099Input'));
const TaxOptimizer = lazy(() => import('@/pages/TaxOptimizer'));
const FixedAssets = lazy(() => import('@/pages/tax/FixedAssets'));
const ScheduleM1 = lazy(() => import('@/pages/tax/ScheduleM1'));
const Form945Page = lazy(() => import('@/pages/tax/Form945'));
const Payroll = lazy(() => import('@/pages/Payroll'));
const CompensationReview = lazy(() => import('@/pages/CompensationReview'));
const Reconcile = lazy(() => import('@/pages/Reconcile'));
const CardReconcile = lazy(() => import('@/pages/CardReconcile'));
const Hours = lazy(() => import('@/pages/Hours'));
const WorkCalendar = lazy(() => import('@/pages/WorkCalendar'));
const Credentials = lazy(() => import('@/pages/Credentials'));
const Query = lazy(() => import('@/pages/Query'));
const ActivityLog = lazy(() => import('@/pages/ActivityLog'));
const Settings = lazy(() => import('@/pages/Settings'));
const NotificationPreferences = lazy(() => import('@/pages/settings/NotificationPreferences'));
const SmsCapture = lazy(() => import('@/pages/settings/SmsCapture'));
const AiUsage = lazy(() => import('@/pages/settings/AiUsage'));
const Security = lazy(() => import('@/pages/settings/Security'));
const CalendarSync = lazy(() => import('@/pages/settings/CalendarSync'));  // Scheduling Phase B-E
const GoogleCalendarCallback = lazy(() => import('@/pages/settings/GoogleCalendarCallback'));  // Scheduling Phase B-E
const More = lazy(() => import('@/pages/More'));
const PersonalTaxSettings = lazy(() => import('@/pages/PersonalTaxSettings'));
const ApiKeys = lazy(() => import('@/pages/ApiKeys'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const PlatformDashboard = lazy(() => import('@/pages/admin/PlatformDashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('@/pages/admin/AdminUserDetail'));
const ArchitectureView = lazy(() => import('@/pages/admin/ArchitectureView'));
const CostEstimate = lazy(() => import('@/pages/admin/CostEstimate'));
const PlatformHealth = lazy(() => import('@/pages/admin/PlatformHealth'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Landing = lazy(() => import('@/pages/Landing'));
const OAuthCallback = lazy(() => import('@/pages/OAuthCallback'));
const EmailIngest = lazy(() => import('@/pages/EmailIngest'));
const SMSIngest = lazy(() => import('@/pages/SMSIngest'));
const MileageTracker = lazy(() => import('@/pages/MileageTracker'));
const Documents = lazy(() => import('@/pages/Documents'));
const ClientsPage = lazy(() => import('@/pages/Clients'));
const InvoicesPage = lazy(() => import('@/pages/Invoices'));
const InvoiceCreate = lazy(() => import('@/pages/InvoiceCreate'));
const InvoiceDetail = lazy(() => import('@/pages/InvoiceDetail'));
const RecurringInvoices = lazy(() => import('@/pages/RecurringInvoices'));
const AgingReport = lazy(() => import('@/pages/AgingReport'));
const ContractorsPage = lazy(() => import('@/pages/Contractors'));
const Section105Page = lazy(() => import('@/pages/Section105'));
const RetirementPlanner = lazy(() => import('@/pages/RetirementPlanner'));
const Billing = lazy(() => import('@/pages/Billing'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const Employees = lazy(() => import('@/pages/hr/Employees'));
const EmployeeDetail = lazy(() => import('@/pages/hr/EmployeeDetail'));
const HireWizard = lazy(() => import('@/pages/hr/HireWizard'));
const PayPeriods = lazy(() => import('@/pages/hr/PayPeriods'));
const PayPeriodStateAllocations = lazy(() => import('@/pages/hr/PayPeriodStateAllocations'));
const EmployeeOnboarding = lazy(() => import('@/pages/hr/EmployeeOnboarding'));
const BulkImportEmployees = lazy(() => import('@/pages/hr/BulkImportEmployees'));
const Bills = lazy(() => import('@/pages/finance/Bills'));
const BillDetail = lazy(() => import('@/pages/finance/BillDetail'));
const CashFlow = lazy(() => import('@/pages/finance/CashFlow'));
const Projects = lazy(() => import('@/pages/projects/Projects'));
const ProjectDetail = lazy(() => import('@/pages/projects/ProjectDetail'));
const Continuity = lazy(() => import('@/pages/continuity/Continuity'));
const Contracts = lazy(() => import('@/pages/contracts/Contracts'));
const ContractDetail = lazy(() => import('@/pages/contracts/ContractDetail'));
const Workflows = lazy(() => import('@/pages/workflows/Workflows'));
const TermsOfService = lazy(() => import('@/pages/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('@/pages/legal/PrivacyPolicy'));
const DPA = lazy(() => import('@/pages/legal/DPA'));
const Register = lazy(() => import('@/pages/Register'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const MFASetup = lazy(() => import('@/pages/MFASetup'));
const Help = lazy(() => import('@/pages/Help'));
const EmployeeActivate = lazy(() => import('@/pages/EmployeeActivate'));
const W9Upload = lazy(() => import('@/pages/W9Upload'));
// S26 Phase 3: Employee Portal full UI
const EmployeeLayout = lazy(() => import('@/components/employee/EmployeeLayout'));
const EmployeeDashboard = lazy(() => import('@/pages/employee/EmployeeDashboard'));
const EmployeeHoursList = lazy(() => import('@/pages/employee/EmployeeHoursList'));
const EmployeeHoursForm = lazy(() => import('@/pages/employee/EmployeeHoursForm'));
const EmployeeHoursDetail = lazy(() => import('@/pages/employee/EmployeeHoursDetail'));
const EmployeeProfile = lazy(() => import('@/pages/employee/EmployeeProfile'));
const EmployeeCalendarSync = lazy(() => import('@/pages/employee/EmployeeCalendarSync'));  // Scheduling Phase B-E, round-2 audit fix
const EmployeeGoogleCalendarCallback = lazy(() => import('@/pages/employee/EmployeeGoogleCalendarCallback'));
const EmployeeDocuments = lazy(() => import('@/pages/employee/EmployeeDocuments'));
const EmployeeNotifications = lazy(() => import('@/pages/employee/EmployeeNotifications'));
const EmployeeCardCharges = lazy(() => import('@/pages/employee/EmployeeCardCharges'));  // Phase 9
// S27 Phase 4: Manager Portal
const ManagerApprovals = lazy(() => import('@/pages/manager/ManagerApprovals'));
const ManagerEmployees = lazy(() => import('@/pages/manager/ManagerEmployees'));
const ManagerEmployeeDetail = lazy(() => import('@/pages/manager/ManagerEmployeeDetail'));
const ManagerCardholderReview = lazy(() => import('@/pages/manager/ManagerCardholderReview'));  // Phase 9
const ManagerSchedule = lazy(() => import('@/pages/manager/ManagerSchedule'));  // Scheduling Phase B-E
// S32 C2: white-label advisor console
const AdvisorLayout = lazy(() => import('@/components/AdvisorLayout').then(m => ({ default: m.AdvisorLayout })));
const AdvisorClients = lazy(() => import('@/pages/advisor/Clients'));
const AdvisorClient = lazy(() => import('@/pages/advisor/Client'));
const AdvisorPortfolio = lazy(() => import('@/pages/advisor/Portfolio'));
const AdvisorSettings = lazy(() => import('@/pages/advisor/Settings'));
const AdvisorEntityDrillIn = lazy(() => import('@/pages/advisor/EntityDrillIn'));
const PersonalOverview = lazy(() => import('@/pages/PersonalOverview'));
const Waitlist = lazy(() => import('@/pages/Waitlist'));
const OperatingIntelligenceDemo = lazy(() => import('@/pages/demo/OperatingIntelligenceDemo'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <SpinnerRing />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 5 * 60 * 1000 },
  },
});

function AppShell() {
  const { theme, entities, themeMode, setThemeMode } = useAppStore();
  const { isEmployee, isManager } = useRole();
  const location = useLocation();
  const navigate = useNavigate();

  // Sprint 1.5 (2026-05-28): if themeMode is 'auto', listen for OS-level
  // dark/light flips and re-resolve. Without this, a user on auto mode
  // wouldn't see Nexus follow their OS at sunset. Listener attaches once;
  // setThemeMode('auto') re-runs the resolver each time the media query
  // changes. Re-attaches if themeMode changes (cleanup on unmount).
  useEffect(() => {
    if (themeMode !== 'auto') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setThemeMode('auto');
    // Safari < 14 doesn't have addEventListener on MediaQueryList
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [themeMode, setThemeMode]);

  // Employee-role users are constrained to /employee/* (S26 Phase 2).
  // Their /auth/me memberships are role='employee', so any deep-link or
  // refresh attempt to /dashboard / /capture / etc. bounces them home.
  // Backend RLS + role checks are the security boundary; this is UX.
  useEffect(() => {
    if (isEmployee && !location.pathname.startsWith('/employee')) {
      navigate('/employee', { replace: true });
    }
  }, [isEmployee, location.pathname, navigate]);

  // S35: inverse of the above — bounce NON-employees AWAY from /employee.
  // Without this, an owner/admin who deep-links to /employee triggers a
  // cascade of 403s from the employee-only API endpoints (getMe,
  // notifications, paystubs). The backend correctly rejects them, but
  // the UI has no graceful render path — better to redirect upfront.
  // Allow public route /employee/activate/:token to pass through (it
  // has no useRole context yet — not authed).
  useEffect(() => {
    if (
      !isEmployee &&
      location.pathname.startsWith('/employee') &&
      !location.pathname.startsWith('/employee/activate')
    ) {
      navigate('/', { replace: true });
    }
  }, [isEmployee, location.pathname, navigate]);

  // Manager-role users (S27 Phase 4) land on /manager/approvals — that's
  // the natural home for someone whose job is approving time entries.
  // Owner/admin can ALSO visit /manager/* (they pass minRole="manager")
  // but don't auto-redirect there. Pure managers get redirected from
  // the company home (/) to their workspace. (The Command Center replaced
  // the old Dashboard at /; the legacy /numbers/legacy route was retired
  // 2026-06-30 — the cash-flow forecast it uniquely hosted moved to
  // /finance/cashflow.)
  useEffect(() => {
    if (isManager && location.pathname === '/') {
      navigate('/manager/approvals', { replace: true });
    }
  }, [isManager, location.pathname, navigate]);

  // Global keyboard shortcuts (Cmd+K, Cmd+P, g-d/e/h/t/c, Esc). No-op while
  // typing in an input, so physical letter keys still reach fields.
  useKeyboardShortcuts();

  // Sidebar P2c (Wave 2, 2026-05-21): Cmd+P opens command palette.
  // Listens for the `nexus:command-palette-open` event dispatched from
  // useKeyboardShortcuts. Local state toggles visibility; CommandPalette
  // component handles the modal + keyboard nav internally.
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Mobile nav drawer (2026-06-02) — the desktop sidebar shown via a top
  // hamburger, so mobile + desktop share the same menu.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    const handleOpen = () => setPaletteOpen(true);
    window.addEventListener('nexus:command-palette-open', handleOpen);
    return () => window.removeEventListener('nexus:command-palette-open', handleOpen);
  }, []);

  // Sync dark mode class with theme state
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // S34: Register service worker + poll for updates + clear caches when
  // the deployed build version differs from what we last saw.
  //
  // Three behaviors stacked here:
  //   (a) Initial register on app mount.
  //   (b) Poll registration.update() every 60s while the tab is alive.
  //       This is what lets a long-running tab notice a new deploy
  //       without requiring a navigation. The SW then enters its
  //       install->waiting flow; UpdatePrompt component picks up the
  //       waiting worker and surfaces the refresh banner.
  //   (c) Version-change check at app startup: fetch /v1/version,
  //       compare to localStorage['nexus_last_seen_version']. If they
  //       differ, this user has loaded an old cached bundle since the
  //       last deploy — call clearAppCaches() before continuing so any
  //       stale Cache Storage entries are wiped. Catches the
  //       silent-token-refresh path (which doesn't go through
  //       clearAppCaches in auth.ts).
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;

    let updateInterval: number | null = null;
    let cancelled = false;

    (async () => {
      // Step 1: version-change check FIRST. If the deployed build has
      // changed since this client last saw it, wipe caches (which
      // unregisters any old SW) BEFORE registering the new one — so we
      // don't accidentally evict our own freshly-registered SW.
      try {
        const r = await fetch('/v1/version', { cache: 'no-store' });
        if (r.ok && !cancelled) {
          const v = await r.json();
          const current = String(v.version || '');
          if (current) {
            const lastSeen = localStorage.getItem('nexus_last_seen_version');
            if (lastSeen && lastSeen !== current) {
              const { clearAppCaches } = await import('@/lib/auth');
              await clearAppCaches();
            }
            localStorage.setItem('nexus_last_seen_version', current);
          }
        }
      } catch { /* network / parse — non-fatal */ }

      if (cancelled) return;

      // Step 2: register the SW (or pick up the one already registered
      // if it survived the cache-clear above) and start the update poll.
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        updateInterval = window.setInterval(() => {
          reg.update().catch(() => { /* transient — try next tick */ });
        }, 60_000);
      } catch (err) {
        console.warn('SW registration failed:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (updateInterval !== null) window.clearInterval(updateInterval);
    };
  }, []);

  return (
    <div className="min-h-dvh bg-slate-50 dark:bg-slate-900">
      <DemoBanner />
      <UpdatePrompt />
      {/* W2-02: post-password-sign-in passkey enrollment offer (self-gating:
          password sign-ins only, supported browsers, non-demo, no local
          passkey, 30-day snooze). Mounted beside UpdatePrompt so it covers
          owner AND manager landings. */}
      <PasskeyEnrollPrompt />
      <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <Header entities={entities} onOpenNav={() => setMobileNavOpen(true)} />
      <MobileNavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <VersionFooter />

      <div className="flex">
        <SideNav />

        {/* min-w-0 is critical: flex-1 alone doesn't allow shrinking below
            content min-width, so a wide table inside any page would force
            <main> wider than viewport at 375px (S37 follow-up finding).
            With min-w-0, flex-1 actually shrinks and per-page tables /
            wide widgets can be wrapped in overflow-x-auto for internal
            scrolling without expanding the parent layout.

            W2-27 A (2026-06-11): max-w 1200 → 1480 + mx-auto (was
            left-aligned, leaving a ~480px dead gutter on 1920px monitors).
            Prose-heavy pages self-cap with max-w-prose; deliberately-narrow
            pages (auth, wizards, legal) keep their own caps. */}
        <main className="flex-1 min-w-0 mx-auto w-full px-4 py-6 lg:px-8 pb-24 lg:pb-6 max-w-[1480px]">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            {/* W2-22: per-page glossary first-use scope — the inline
                "(Qualified Business Income)" expansion renders once per
                PAGE; navigating resets the scope via the pathname key. */}
            <GlossaryProvider scopeKey={location.pathname}>
            <Routes>
              <Route path="/" element={<CommandCenter />} />
              {/* Home Redesign (2026-06-02) — the "You" lens. Owner/admin only
                  (RoleGuard prevents the component mounting + the /me/overview
                  fetch for staff). The LensSwitch in the header toggles / ⇄ /me.
                  Mounted inside AppShell (reuses header + sidebar). */}
              <Route path="/me" element={<RoleGuard minRole="admin"><PersonalOverview /></RoleGuard>} />
              {/* /numbers/legacy retired (dead-code audit 6/6, 2026-06-30): the
                  old Dashboard's home role moved to the Command Center at /, so
                  redirect any stale bookmark there instead of a dead-end 404.
                  Manager/employee role redirects still fire from / afterward. */}
              <Route path="/numbers/legacy" element={<Navigate to="/" replace />} />
              {/* Frontend Cycle Fix (2026-05-23) — Agent 1 P1-1: Capture POSTs
                  through /ingest/smart which Cross-Lambda Cycle 1 gated to
                  bookkeeper+ at the supervisor forward layer. Without this
                  RoleGuard, viewer/employee/manager can land on the page,
                  scan a receipt, and Submit silently 403s with no toast. */}
              <Route path="/capture" element={<RoleGuard minRole="bookkeeper"><Capture /></RoleGuard>} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/compliance" element={<Compliance />} />
              <Route path="/compliance/checklist" element={<RoleGuard minRole="accountant"><ComplianceChecklist /></RoleGuard>} />
              <Route path="/compliance/facts" element={<RoleGuard minRole="viewer"><ComplianceFacts /></RoleGuard>} />
              <Route path="/locums/classifications" element={<RoleGuard minRole="viewer"><LocumsClassifications /></RoleGuard>} />
              <Route path="/locums/classifications/:id" element={<RoleGuard minRole="viewer"><LocumsClassificationDetail /></RoleGuard>} />
              <Route path="/locums/classify" element={<RoleGuard minRole="bookkeeper"><LocumsClassify /></RoleGuard>} />
              <Route path="/reports" element={<RoleGuard minRole="accountant"><Reports /></RoleGuard>} />
              <Route path="/consolidated" element={<RoleGuard minRole="admin"><Consolidated /></RoleGuard>} />
              <Route path="/documents/view/:source/:id" element={<DocumentView />} />
              <Route path="/tax" element={<RoleGuard minRole="accountant"><TaxPlanning /></RoleGuard>} />
              <Route path="/tax/1099-input" element={<RoleGuard minRole="accountant"><Tax1099Input /></RoleGuard>} />
              <Route path="/tax/optimizer" element={<RoleGuard minRole="admin"><TaxOptimizer /></RoleGuard>} />
              <Route path="/tax/fixed-assets" element={<RoleGuard minRole="bookkeeper"><FixedAssets /></RoleGuard>} />
              <Route path="/tax/schedule-m1" element={<RoleGuard minRole="bookkeeper"><ScheduleM1 /></RoleGuard>} />
              <Route path="/tax/form-945" element={<RoleGuard minRole="viewer"><Form945Page /></RoleGuard>} />
              <Route path="/hours" element={<Hours />} />
              <Route path="/hours/calendar" element={<WorkCalendar />} />
              <Route path="/credentials" element={<Credentials />} />
              <Route path="/payroll" element={<RoleGuard minRole="admin"><Payroll /></RoleGuard>} />
              <Route path="/compensation-review" element={<RoleGuard minRole="admin"><CompensationReview /></RoleGuard>} />
              <Route path="/finance/reconcile" element={<RoleGuard minRole="admin"><Reconcile /></RoleGuard>} />
              <Route path="/finance/reconcile/:id" element={<RoleGuard minRole="admin"><Reconcile /></RoleGuard>} />
              <Route path="/finance/card-statements" element={<RoleGuard minRole="admin"><CardReconcile /></RoleGuard>} />
              <Route path="/finance/card-statements/:id" element={<RoleGuard minRole="admin"><CardReconcile /></RoleGuard>} />
              {/* Cash-flow forecast — rehomed 2026-06-30 from the retired
                  /numbers/legacy Dashboard. viewer+ to match the backend
                  ensureFinanceReadAccess on GET /finance/cashflow. */}
              <Route path="/finance/cashflow" element={<RoleGuard minRole="viewer"><CashFlow /></RoleGuard>} />
              <Route path="/query" element={<Query />} />
              {/* Sprint 1.5 (2026-05-28): Ask Nexus search bar in the topbar
                  navigates to /ask. Same surface as /query — alias kept for
                  any deep-link that exists in user docs / older bookmarks. */}
              <Route path="/ask" element={<Query />} />
              <Route path="/activity" element={<ActivityLog />} />
              <Route path="/settings" element={<RoleGuard minRole="owner"><Settings /></RoleGuard>} />
              <Route path="/settings/notifications" element={<NotificationPreferences />} />
              <Route path="/settings/sms" element={<SmsCapture />} />
              <Route path="/settings/personal-tax" element={<RoleGuard minRole="owner"><PersonalTaxSettings /></RoleGuard>} />
              <Route path="/settings/api-keys" element={<RoleGuard minRole="admin"><ApiKeys /></RoleGuard>} />
              {/* Sprint 1.5 — Settings drilldown stubs (per operator decision Q1 MIDDLE: AI usage + Security, drop Preferences + Integrations) */}
              {/* 2026-06-17: owner→admin so the admin-level AI-budget alert
                  (nav-attention + Home card) isn't a dead end. Backend GET
                  /ai/usage already allows any entity member; cap changes /
                  top-ups still route to owner-only Billing. */}
              <Route path="/settings/ai-usage" element={<RoleGuard minRole="admin"><AiUsage /></RoleGuard>} />
              {/* W2-02: passkey management is a PERSONAL credential surface —
                  every signed-in role manages their own passkeys/MFA, so no
                  role floor (was minRole="owner", which locked staff out of
                  their own device security). */}
              <Route path="/settings/security" element={<Security />} />
              {/* Scheduling Phase B-E (2026-07-14): personal calendar sync —
                  same "no role floor" reasoning as /settings/security, every
                  signed-in entity member manages their OWN connections. */}
              <Route path="/settings/calendar-sync" element={<CalendarSync />} />
              <Route path="/settings/calendar-sync/google/callback" element={<GoogleCalendarCallback />} />
              <Route path="/more" element={<More />} />
              {/* Admin — nested under AdminLayout which adds MFA step-up gate on top of RoleGuard */}
              <Route path="/admin" element={<RoleGuard minRole="platform_admin"><AdminLayout /></RoleGuard>}>
                <Route path="platform" element={<PlatformDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="users/:id" element={<AdminUserDetail />} />
                <Route path="architecture" element={<ArchitectureView />} />
                <Route path="costs" element={<CostEstimate />} />
                {/* Sprint 1.5 — NEW Platform health stub per operator decision Q4 */}
                <Route path="health" element={<PlatformHealth />} />
              </Route>
              {/* Frontend Cycle Fix (2026-05-23) — Agent 1 P1-5: /ingest/email
                  + /ingest/sms are setup-flow pages whose submission POSTs go
                  through supervisor's /ingest forward gate (bookkeeper+).
                  Without route guards, viewer/employee/manager configure
                  forwarding rules that silently 403 on submit. */}
              <Route path="/ingest/email" element={<RoleGuard minRole="bookkeeper"><EmailIngest /></RoleGuard>} />
              <Route path="/ingest/sms" element={<RoleGuard minRole="bookkeeper"><SMSIngest /></RoleGuard>} />
              <Route path="/mileage" element={<MileageTracker />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              {/* Frontend Cycle Fix (2026-05-23) — Agent 1 P1-7: /invoices/new
                  is a pure form-create page. POST requires bookkeeper+ at
                  supervisor. Route-guard appropriate; viewer/manager have no
                  reason to land on the form-only page. /invoices list page
                  stays open to viewer (Create CTA hidden inline). */}
              <Route path="/invoices/new" element={<RoleGuard minRole="bookkeeper"><InvoiceCreate /></RoleGuard>} />
              <Route path="/invoices/recurring" element={<RecurringInvoices />} />
              <Route path="/invoices/aging" element={<AgingReport />} />
              <Route path="/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/contractors" element={<ContractorsPage />} />
              <Route path="/section-105" element={<Section105Page />} />
              <Route path="/retirement" element={<RetirementPlanner />} />
              <Route path="/billing" element={<RoleGuard minRole="owner"><Billing /></RoleGuard>} />
              {/* /pricing is mounted in the public Routes block below
                  (post-2026-05-20). When authed users navigate to /pricing
                  they hit the same public route — Pricing.tsx reads `user`
                  from the store and routes accordingly (signed-in: direct
                  Stripe Checkout; signed-out: /login?signup=1). */}
              {/* HR (Phase 2a). Employees + W-4/I-9 + PTO ledger.
                  Owner role required to write; admin / bookkeeper read. */}
              <Route path="/hr" element={<RoleGuard minRole="bookkeeper"><Employees /></RoleGuard>} />
              {/* /hr/pay-periods AND /hr/new MUST come before /hr/:id so the literal path wins. */}
              <Route path="/hr/pay-periods" element={<RoleGuard minRole="admin"><PayPeriods /></RoleGuard>} />
              <Route path="/hr/pay-periods/:id/state-allocations" element={<RoleGuard minRole="admin"><PayPeriodStateAllocations /></RoleGuard>} />
              <Route path="/hr/new" element={<RoleGuard minRole="admin"><HireWizard /></RoleGuard>} />
              <Route path="/hr/bulk-import" element={<RoleGuard minRole="admin"><BulkImportEmployees /></RoleGuard>} />
              <Route path="/hr/:id" element={<RoleGuard minRole="bookkeeper"><EmployeeDetail /></RoleGuard>} />
              <Route path="/hr/employees/:id/onboarding" element={<RoleGuard minRole="bookkeeper"><EmployeeOnboarding /></RoleGuard>} />
              {/* Finance: AP (Phase 2b). Bills + payments. Read-access at
                  viewer (board / accountant read-through): the list and detail
                  expose vendor names + amounts but no export paths. Write +
                  payment mutations are gated server-side by role. */}
              <Route path="/finance/bills" element={<RoleGuard minRole="viewer"><Bills /></RoleGuard>} />
              <Route path="/finance/bills/:id" element={<RoleGuard minRole="viewer"><BillDetail /></RoleGuard>} />
              {/* Projects + per-project profitability (Phase 2c). Same
                  viewer-read posture as /finance/bills - profitability and
                  utilization are the board's read-through; mutations are
                  still gated server-side. */}
              <Route path="/projects" element={<RoleGuard minRole="viewer"><Projects /></RoleGuard>} />
              <Route path="/projects/:id" element={<RoleGuard minRole="viewer"><ProjectDetail /></RoleGuard>} />
              {/* Owner Continuity (Phase 2d). Owner-only: this is the succession
                  packet, not something bookkeepers / accountants should modify. */}
              <Route path="/continuity" element={<RoleGuard minRole="owner"><Continuity /></RoleGuard>} />
              {/* Contracts (Phase 2e). CLM-lite: leases, licenses, insurance,
                  retainer letters, any counterparty agreement. Viewer read-through
                  of the vault; owner-level required to mutate so a demo visitor
                  can see depth without editing. */}
              <Route path="/contracts" element={<RoleGuard minRole="viewer"><Contracts /></RoleGuard>} />
              <Route path="/contracts/:id" element={<RoleGuard minRole="viewer"><ContractDetail /></RoleGuard>} />
              {/* Workflow automation (Phase 2f). Approval rules + queue +
                  history. Bookkeeper+ to see; owner-level to manage rules +
                  decide approvals (server-side gated by the rule's
                  required_role; this is the UI gate only). */}
              <Route path="/workflows" element={<RoleGuard minRole="bookkeeper"><Workflows /></RoleGuard>} />
              {/* Manager Portal (S27 Phase 4). Manager role + admin/owner.
                  Server scopes by employee_manager_assignments. Manager
                  can decide their assigned employees' time-entry approvals;
                  admin/owner bypass the assignment scope server-side.
                  ManagerRouteGuard (not the generic RoleGuard) on purpose:
                  manager sits BELOW viewer in ROLE_LEVEL, so a plain
                  minRole="manager" hierarchy check let bookkeeper/accountant/
                  viewer land on this page and then get every API call
                  rejected by the backend's requireManagerAccess with a
                  confusing ROLE_MISMATCH. This guard mirrors that backend
                  check exactly (isManager || isAdmin). */}
              <Route path="/manager/approvals" element={<ManagerRouteGuard><ManagerApprovals /></ManagerRouteGuard>} />
              <Route path="/manager/employees" element={<ManagerRouteGuard><ManagerEmployees /></ManagerRouteGuard>} />
              <Route path="/manager/employees/:id" element={<ManagerRouteGuard><ManagerEmployeeDetail /></ManagerRouteGuard>} />
              <Route path="/manager/cardholder" element={<ManagerRouteGuard><ManagerCardholderReview /></ManagerRouteGuard>} />
              {/* Scheduling Phase B-E (2026-07-14): serves BOTH audiences from
                  one route — owner/admin get the entity-wide view, a pure
                  manager gets their assigned-employees view, both via the
                  SAME requireManagerAccess server scoping. See MGR_SCHEDULE /
                  SCHEDULE_TEAM in nav-layouts.ts for the two nav entries that
                  both point here. */}
              <Route path="/manager/schedule" element={<ManagerRouteGuard><ManagerSchedule /></ManagerRouteGuard>} />
              <Route path="/help" element={<Help />} />
              {/* /employee/* is mounted at the top-level Routes (not inside
                  AppShell) because EmployeeLayout has its own chrome. The
                  AppShell-level redirect in useEffect above sends role=employee
                  to /employee, where EmployeeLayout takes over. */}
              {/* W2-15: catch-all inside the shell — a bad/stale URL renders a
                  friendly NotFound (was a blank content area). */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </GlossaryProvider>
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>

      <BottomNav onOpenNav={() => setMobileNavOpen(true)} />
      <VoiceFAB />
      <DevToolbar />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/mfa-setup" element={<MFASetup />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/welcome" element={<Landing />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/demo/operating-intelligence" element={<OperatingIntelligenceDemo />} />
            {/* /pricing is PUBLIC (moved from authed AppShell on 2026-05-20).
                Pricing.tsx works unauthed via FALLBACK_PLANS; clicking a
                Start button when signed-out routes to /login?signup=1.
                Required for Stripe go-live + any marketing/SEO surface. */}
            <Route path="/pricing" element={<Pricing />} />
            {/* Employee Portal activation (PUBLIC). Single-use token from
                the invitation email creates a Cognito user + signs them in,
                then redirects to /employee (which IS authed). S26 Phase 2. */}
            <Route path="/employee/activate/:token" element={<EmployeeActivate />} />
            {/* S33 P1: public W-9 upload (no auth — token in URL is the gate).
                Single-use, salted-hashed, 7-day TTL. Sent to contractors via
                Mailgun email. */}
            <Route path="/w9-upload/:token" element={<W9Upload />} />
            {/* Employee Portal authed routes (S26 Phase 3). Mounted at the
                top level so EmployeeLayout's own chrome (top bar + sidenav)
                doesn't double up with AppShell's. ProtectedRoute checks
                tokens; EmployeeLayout's getMe() check verifies the user is
                actually an active employee in their entity (server gate). */}
            <Route path="/employee" element={<ProtectedRoute><EmployeeLayout /></ProtectedRoute>}>
              <Route index element={<EmployeeDashboard />} />
              <Route path="hours" element={<EmployeeHoursList />} />
              <Route path="hours/new" element={<EmployeeHoursForm />} />
              <Route path="hours/:id" element={<EmployeeHoursDetail />} />
              <Route path="profile" element={<EmployeeProfile />} />
              <Route path="documents" element={<EmployeeDocuments />} />
              <Route path="documents/:id/view" element={<EmployeeDocumentView />} />
              <Route path="cards" element={<EmployeeCardCharges />} />
              <Route path="notifications" element={<EmployeeNotifications />} />
              {/* Scheduling Phase B-E round-2 audit fix: personal calendar
                  sync needs to be reachable from the Employee Portal too —
                  see EmployeeCalendarSync.tsx's docblock. Nested here (not a
                  standalone top-level route) so it keeps EmployeeLayout's
                  chrome and isn't force-redirected by the isEmployee guard. */}
              <Route path="calendar-sync" element={<EmployeeCalendarSync />} />
              <Route path="calendar-sync/google/callback" element={<EmployeeGoogleCalendarCallback />} />
            </Route>
            {/* S32 C2: white-label advisor console. Mounted at top level so
                AdvisorLayout's own chrome (firm-branded header) doesn't double
                up with AppShell. Server scopes by advisor_org_links — calling
                any /advisor/* endpoint without an active link returns 404
                (empty list) or 403 (drill-in to unlinked org). */}
            <Route path="/advisor" element={<ProtectedRoute><AdvisorLayout /></ProtectedRoute>}>
              <Route index element={<AdvisorClients />} />
              <Route path="clients" element={<AdvisorClients />} />
              <Route path="clients/:orgId" element={<AdvisorClient />} />
              <Route path="clients/:orgId/entities/:eid" element={<AdvisorEntityDrillIn />} />
              <Route path="portfolio" element={<AdvisorPortfolio />} />
              <Route path="settings" element={<AdvisorSettings />} />
            </Route>
            {/* Legal pages (public). Currently DRAFT - the LegalLayout shows a
                prominent "awaiting legal review" banner. Do not link from
                public marketing surfaces until counsel-reviewed. */}
            <Route path="/legal/terms" element={<TermsOfService />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/dpa" element={<DPA />} />
            <Route path="/*" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
